import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Suspense, useState } from 'react'
import {
  type Company,
  companyQueryOptions,
  useArchiveCompany,
  useUpdateCompany,
} from '@/entities/company'
import { ProjectSummary, projectsQueryOptions } from '@/entities/project'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { ContactDialog, companyToInput, LinkDialog } from '@/features/manage-company'
import { CreateProjectDialog } from '@/features/manage-project'
import { useSearchStore } from '@/features/search'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { InlineText } from '@/shared/ui/inline-edit'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { VisibilityMatrix } from '@/widgets/visibility-matrix'

export const Route = createFileRoute('/_app/companies/$companyId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(companyQueryOptions(params.companyId)),
  component: CompanyPage,
})

function CompanyPage() {
  const { companyId } = Route.useParams()
  const company = useQuery(companyQueryOptions(companyId))

  if (company.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (company.isError || !company.data) {
    return (
      <StatusScreen title="Компания не найдена" message="Ссылка устарела или компания удалена.">
        <Link to="/companies">
          <Button variant="secondary">К списку компаний</Button>
        </Link>
      </StatusScreen>
    )
  }
  return <CompanyBody company={company.data} />
}

function CompanyBody({ company }: { company: Company }) {
  const { isAdmin } = useSession()
  const openWithTag = useSearchStore((s) => s.openWithTag)
  const update = useUpdateCompany(company.id)
  const archive = useArchiveCompany(company.id)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  // null — закрыто, {index:null} — добавление, {index:n} — правка.
  const [contact, setContact] = useState<{ index: number | null } | null>(null)
  const [link, setLink] = useState<{ index: number | null } | null>(null)

  const save = (overrides: Parameters<typeof companyToInput>[1]) =>
    update.mutateAsync(companyToInput(company, overrides))

  return (
    <>
      <header className="mb-6 flex flex-col">
        {company.archived && (
          <div className="mb-2">
            <Badge tone="warning">В архиве</Badge>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <h1 className="t-h2 min-w-0 flex-1 basis-56 text-text-primary">
            <InlineText
              value={company.name}
              ariaLabel="Название компании"
              canEdit={isAdmin}
              placeholder="Название"
              emptyText="Без названия"
              onSave={(v) => save({ name: v })}
            />
          </h1>
          {isAdmin && (
            <Button
              variant={company.archived ? 'secondary' : 'danger'}
              onClick={() => (company.archived ? archive.mutate(false) : setArchiveOpen(true))}
            >
              {company.archived ? 'Вернуть из архива' : 'В архив'}
            </Button>
          )}
        </div>
        <div className="mt-3 text-sm text-text-secondary">
          <InlineText
            value={company.legal_name}
            ariaLabel="Юридическое название"
            canEdit={isAdmin}
            placeholder="ООО «…»"
            emptyText="Юр. название не указано"
            onSave={(v) => save({ legal_name: v })}
          />
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Реквизиты">
          <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-3 text-sm">
            <Requisite
              label="ИНН"
              value={company.inn}
              canEdit={isAdmin}
              onSave={(v) => save({ inn: v })}
            />
            <Requisite
              label="КПП"
              value={company.kpp}
              canEdit={isAdmin}
              onSave={(v) => save({ kpp: v })}
            />
            <Requisite
              label="ОГРН"
              value={company.ogrn}
              canEdit={isAdmin}
              onSave={(v) => save({ ogrn: v })}
            />
            <Requisite
              label="Юр. адрес"
              value={company.legal_address}
              canEdit={isAdmin}
              onSave={(v) => save({ legal_address: v })}
            />
            <Requisite
              label="Банк"
              value={company.bank_details}
              canEdit={isAdmin}
              multiline
              onSave={(v) => save({ bank_details: v })}
            />
          </dl>
        </Card>

        <Card title="Видимость в сервисах">
          <Suspense fallback={<Spinner />}>
            <VisibilityMatrix kind="company" id={company.id} canEdit={isAdmin} />
          </Suspense>
        </Card>

        <Card
          title="Контакты"
          actions={
            isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => setContact({ index: null })}>
                + Контакт
              </Button>
            )
          }
        >
          {company.contacts.length === 0 ? (
            <p className="text-xs text-text-secondary">Контактов нет</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {company.contacts.map((c, i) => {
                const meta = [c.job_title, c.phone, c.email].filter(Boolean).join(' · ')
                const body = (
                  <>
                    <div className="font-medium text-text-primary">{c.name}</div>
                    {meta && <div className="text-xs text-text-secondary">{meta}</div>}
                  </>
                )
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: контакты — упорядоченный список без стабильного id
                  <li key={i}>
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => setContact({ index: i })}
                        className="-mx-2 w-full rounded-control px-2 py-1.5 text-left transition-colors hover:bg-bg-elevated"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="px-2 py-1.5">{body}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Ссылки"
          actions={
            isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => setLink({ index: null })}>
                + Ссылка
              </Button>
            )
          }
        >
          {company.links.length === 0 ? (
            <p className="text-xs text-text-secondary">Ссылок нет</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {company.links.map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: ссылки — упорядоченный список без стабильного id
                <li key={i} className="flex items-center gap-2">
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 truncate text-sm text-accent hover:underline"
                  >
                    {l.title || l.url}
                  </a>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Редактировать ссылку «${l.title || l.url}»`}
                      onClick={() => setLink({ index: i })}
                    >
                      Изм.
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Теги" className="mt-6">
        <EntityTagsEditor entityKind="company" entityId={company.id} onTagClick={openWithTag} />
      </Card>

      <Card
        title="Проекты"
        className="mt-6"
        actions={
          <Button size="sm" onClick={() => setCreateProjectOpen(true)}>
            Новый проект
          </Button>
        }
      >
        <CompanyProjects companyId={company.id} />
      </Card>

      <CreateProjectDialog
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        companyId={company.id}
      />
      <ContactDialog
        company={company}
        index={contact?.index ?? null}
        open={contact !== null}
        onClose={() => setContact(null)}
      />
      <LinkDialog
        company={company}
        index={link?.index ?? null}
        open={link !== null}
        onClose={() => setLink(null)}
      />
      <ConfirmDialog
        open={archiveOpen}
        title="В архив"
        message={`Компания «${company.name}» будет скрыта из активных списков. Данные сохранятся.`}
        confirmLabel="В архив"
        confirmVariant="danger"
        pending={archive.isPending}
        onConfirm={() => archive.mutate(true, { onSuccess: () => setArchiveOpen(false) })}
        onCancel={() => setArchiveOpen(false)}
      />
    </>
  )
}

function Requisite({
  label,
  value,
  canEdit,
  multiline = false,
  onSave,
}: {
  label: string
  value: string
  canEdit: boolean
  multiline?: boolean
  onSave: (next: string) => Promise<unknown>
}) {
  return (
    <>
      <dt className="text-text-secondary">{label}</dt>
      <dd className="min-w-0 break-words">
        <InlineText
          value={value}
          ariaLabel={label}
          canEdit={canEdit}
          multiline={multiline}
          onSave={onSave}
        />
      </dd>
    </>
  )
}

function CompanyProjects({ companyId }: { companyId: string }) {
  const projects = useQuery(projectsQueryOptions({ companyId }))
  const items = projects.data ?? []

  if (projects.isPending) return <Spinner />
  if (items.length === 0) return <p className="text-xs text-text-secondary">Проектов нет</p>

  return (
    // -mx-3 гасит собственные паддинги строки — текст выравнивается по краю карточки.
    <ul className="-mx-3 flex flex-col divide-y divide-border-subtle">
      {items.map((project) => (
        <li key={project.id}>
          <Link to="/projects/$projectId" params={{ projectId: project.id }} className="block">
            <ProjectSummary project={project} />
          </Link>
        </li>
      ))}
    </ul>
  )
}
